import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getTeams } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Users, Target, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TeamsPage = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getTeams();
        setTeams(res.data);
      } catch (error) {
        console.error('Error fetching teams:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getPhotoUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${API}/api/files/${path}`;
  };

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="teams-page">
      <section className="section-padding bg-surface">
        <div className="container-max">
          <h1 className="text-4xl md:text-5xl font-heading font-bold uppercase tracking-tighter mb-4">
            Unsere <span className="text-primary">Mannschaften</span>
          </h1>
          <p className="text-text-secondary max-w-2xl">
            Lerne unsere Mannschaften kennen und erfahre mehr über unsere Spieler.
          </p>
        </div>
      </section>

      <section className="section-padding">
        <div className="container-max">
          {loading ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {[1, 2].map((i) => (
                <Card key={i} className="card-surface animate-pulse">
                  <CardContent className="p-8"><div className="h-32 bg-white/10 rounded" /></CardContent>
                </Card>
              ))}
            </div>
          ) : teams.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {teams.map((team) => (
                <Link key={team.team_id} to={`/mannschaften/${team.team_id}`} data-testid={`team-card-${team.team_id}`}>
                  <Card className="card-surface hover:border-primary/50 transition-all duration-300 h-full overflow-hidden">
                    <CardContent className="p-0">
                      <div className="flex items-center gap-6 p-6">
                        {team.photo_url ? (
                          <img src={getPhotoUrl(team.photo_url)} alt={team.name} className="w-24 h-24 object-contain rounded-lg bg-white/5 p-2" />
                        ) : (
                          <div className="w-24 h-24 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Target className="w-12 h-12 text-primary" />
                          </div>
                        )}
                        <div className="flex-1">
                          <h2 className="text-2xl font-heading font-bold uppercase tracking-tight mb-2">
                            {team.name}
                          </h2>
                          {team.league && (
                            <Badge variant="outline" className="border-accent text-accent mb-2">
                              {team.league}
                            </Badge>
                          )}
                          {team.description && (
                            <p className="text-text-secondary text-sm line-clamp-2">{team.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="border-t border-white/5 px-6 py-3 flex items-center justify-between bg-white/[0.02]">
                        <div className="flex items-center gap-2 text-text-secondary text-sm">
                          <Users className="w-4 h-4" />
                          <span>Mannschaft ansehen</span>
                        </div>
                        <ChevronRight className="w-4 h-4 text-primary" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Target className="w-16 h-16 text-text-secondary mx-auto mb-4" />
              <p className="text-text-secondary">Noch keine Mannschaften vorhanden.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default TeamsPage;
